import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../cn.js';
import { Spinner } from './spinner.js';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
type Size = 'sm' | 'md';

const base =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium select-none ' +
  'transition-colors duration-100 disabled:pointer-events-none disabled:opacity-50 ' +
  '[&_svg]:size-3.5 [&_svg]:shrink-0';

const variants: Record<Variant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-hover shadow-xs',
  secondary: 'bg-bg text-fg border border-line-strong hover:bg-subtle shadow-xs',
  ghost: 'text-fg-2 hover:bg-muted hover:text-fg',
  danger: 'bg-danger text-white hover:bg-danger-hover shadow-xs',
  link: 'text-brand hover:underline underline-offset-2 px-0! h-auto!',
};

const sizes: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-xs rounded-md',
  md: 'h-8 px-3 text-sm rounded-md',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, asChild, className, children, disabled, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(base, variants[variant], sizes[size], className)}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Spinner /> : icon}
          {children}
        </>
      )}
    </Comp>
  );
});

export const IconButton = forwardRef<HTMLButtonElement, ButtonProps & { label: string }>(function IconButton(
  { label, className, size = 'md', variant = 'ghost', ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      aria-label={label}
      title={label}
      variant={variant}
      size={size}
      className={cn(size === 'sm' ? 'w-7 px-0' : 'w-8 px-0', className)}
      {...props}
    />
  );
});
